import { computed, DestroyRef, effect, ElementRef, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core'
import { FormControl } from '@angular/forms'
import { fromEvent } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AccordionValue } from 'primeng/types/accordion'

/**
 * One entry of a detailview's section navigation.
 */
export interface NavSection {
  /** Label shown in the section dropdown */
  label: string
  /** Position of the top-level section; also its accordion panel value */
  mainIndex: number
  /** Accordion value of a sub-panel inside the section; absent for the section itself */
  subValue?: string
}

/**
 * Registers the elements as additional scroll targets while they are rendered and unregisters them
 * when they go away. Must be called in an injection context, e.g. a constructor.
 *
 * @param nav - Navigation service to register the elements with
 * @param panels - Signal of the elements, typically from `viewChildren()`
 */
export function registerNavPanels(nav: DetailviewNavigationService, panels: Signal<readonly ElementRef<HTMLElement>[]>): void {
  effect(() => {
    const current = panels()
    current.forEach(p => nav.registerAdditionalPanel(p))
    return () => {
      current.forEach(p => nav.unregisterAdditionalPanel(p))
    }
  })
}

/**
 * Section navigation of a detailview: keeps the section dropdown, the open accordion panels and the
 * scroll position in sync. Provided per detailview component.
 */
@Injectable()
export class DetailviewNavigationService {
  /** Index of the current section in `sectionHeadings` */
  public readonly currentIndex = signal(0)
  /** Accordion values of the open top-level panels */
  public readonly activePanels = signal<string[]>(['0'])
  /** Sub-panel the navigation last jumped to; coverage forms open it */
  public readonly subSectionTarget = signal<{ mainIndex: number, subValue: string } | null>(null)
  /** Whether the page is scrolled far enough to offer "scroll to top" */
  public readonly showScrollToTop = signal(false)
  /** All nav sections in display order */
  public readonly sectionHeadings = signal<NavSection[]>([])
  /** Options of the section dropdown, valued by index */
  public readonly sectionOptions = computed(() => this.sectionHeadings().map((section, index) => ({
    label: section.label,
    value: index.toString()
  })))

  private readonly panels: WritableSignal<readonly ElementRef<HTMLElement>[]> = signal([])
  private readonly additionalPanels = signal<ElementRef<HTMLElement>[]>([])
  /** Scroll targets of all sections: the detailview's own panels and the registered additional ones */
  public readonly allPanels = computed(() => [...this.panels(), ...this.additionalPanels()])
  private observer: IntersectionObserver | null = null
  private sectionControl?: FormControl<string | null>
  private readonly destroyRef = inject(DestroyRef)
  private readonly injector = inject(Injector)
  private navigationLockTimeout: ReturnType<typeof setTimeout> | null = null

  /**
   * Sets the open top-level panels. Accepts PrimeNG's full ModelSignal union type and normalises it to string[].
   *
   * @param value - Accordion value as emitted by PrimeNG
   */
  public setActivePanels(value: AccordionValue): void {
    this.activePanels.set(this.toStringArray(value))
  }

  /**
   * Replaces the nav sections.
   *
   * @param sectionHeadings - Nav sections in display order
   */
  public setSectionHeadings(sectionHeadings: NavSection[]): void {
    this.sectionHeadings.set(sectionHeadings)
  }

  /**
   * Finds the nav index of a section or sub-panel, used as `data-index` of its scroll target.
   *
   * @param mainIndex - Position of the top-level section
   * @param subValue - Accordion value of the sub-panel; omit for the section itself
   * @returns Index in `sectionHeadings`, or -1 if there is no such section
   */
  public getSectionIndex(mainIndex: number, subValue?: string): number {
    return this.sectionHeadings().findIndex(s => s.mainIndex === mainIndex && s.subValue === subValue)
  }

  /**
   * Adds a scroll target rendered outside the detailview's own template, e.g. by a coverage form.
   *
   * @param panel - Scroll target element carrying a `data-index`
   */
  public registerAdditionalPanel(panel: ElementRef<HTMLElement>): void {
    this.additionalPanels.update(prev => [...prev, panel])
  }

  /**
   * Removes a scroll target added with `registerAdditionalPanel`.
   *
   * @param panel - Scroll target element to remove
   */
  public unregisterAdditionalPanel(panel: ElementRef<HTMLElement>): void {
    this.additionalPanels.update(prev => prev.filter(p => p !== panel))
  }

  /**
   * Connects the navigation to the detailview once its view exists: the section dropdown, the
   * top-level scroll targets, the scroll spy and the scroll-to-top listener.
   *
   * @param control - Form control of the section dropdown
   * @param panelsSignal - Scroll targets of the top-level sections, in section order
   */
  public initialize(
    control: FormControl<string | null>,
    panelsSignal: Signal<readonly ElementRef<HTMLElement>[]>
  ): void {
    this.sectionControl = control

    effect(() => {
      this.panels.set(panelsSignal())
    }, { injector: this.injector })

    if (this.sectionControl.value === null) {
      this.sectionControl.setValue('0', { emitEvent: false })
    }

    this.initScrollSpy()
    this.initScrollListener()
  }

  /** Jumps to the first section. */
  public scrollToTop(): void {
    this.goToSection(0)
  }

  /**
   * Jumps to a section: opens its panel, marks a sub-panel as target for the coverage forms and
   * scrolls it into view.
   *
   * @param index - Index in `sectionHeadings`
   */
  public goToSection(index: number): void {
    const section = this.sectionHeadings()[index]
    if (!section) return

    const targetValue = section.mainIndex.toString()
    const alreadyOpen = this.activePanels().includes(targetValue)
    const alreadyCurrent = this.currentIndex() === index
    if (alreadyCurrent && alreadyOpen && this.subSectionTarget() === null
      && (section.subValue === null || section.subValue === undefined || section.subValue === '')) {
      return
    }

    if (!alreadyOpen) {
      this.activePanels.update(prev => [...prev, targetValue])
    }

    if (section.subValue !== null && section.subValue !== undefined && section.subValue !== '') {
      this.subSectionTarget.set({ mainIndex: section.mainIndex, subValue: section.subValue })
    } else {
      this.subSectionTarget.set(null)
    }

    this.currentIndex.set(index)
    this.sectionControl?.setValue(index.toString(), { emitEvent: false })

    // Suppress the scroll spy for a short window so that intermediate sections
    // entering the viewport during a smooth scroll do not override currentIndex.
    this.lockNavigation()

    // Direct DOM access: the scroll target is found by its data-index; a section without one falls back to its
    // top-level panel.
    const targetEl = this.allPanels().find(p => p.nativeElement.dataset['index'] === index.toString())
    if (targetEl) {
      targetEl.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      const el = this.panels()[section.mainIndex]?.nativeElement
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  /** Stops the scroll spy; call when the detailview is destroyed so the observer does not keep the panels alive. */
  public destroy(): void {
    this.observer?.disconnect()
  }

  /** Jumps to the section before the current one, if any. */
  public goToPreviousSection(): void {
    const current = this.currentIndex()
    if (current > 0) {
      this.goToSection(current - 1)
    }
  }

  /** Jumps to the section after the current one, if any. */
  public goToNextSection(): void {
    const current = this.currentIndex()
    if (current < this.sectionOptions().length - 1) {
      this.goToSection(current + 1)
    }
  }

  private lockNavigation(durationMs = 1000): void {
    if (this.navigationLockTimeout !== null) {
      clearTimeout(this.navigationLockTimeout)
    }
    this.navigationLockTimeout = setTimeout(() => {
      this.navigationLockTimeout = null
    }, durationMs)
  }

  private toStringArray(value: AccordionValue): string[] {
    if (value === null) return []
    if (Array.isArray(value)) return value.map(String)
    return [String(value)]
  }

  private initScrollListener(): void {
    // Debounced so a scroll does not update the signal on every frame; ends with the service via takeUntilDestroyed.
    fromEvent(globalThis, 'scroll').pipe(
      debounceTime(100),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const show = window.scrollY > 280
      if (this.showScrollToTop() !== show) {
        this.showScrollToTop.set(show)
      }
    })
  }

  private initScrollSpy(): void {
    const options: IntersectionObserverInit = {
      root: null, // Use the viewport as the root
      rootMargin: '-10% 0px -70% 0px',
      threshold: 0
    }
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement
          const indexAttr = target.dataset['index'] ?? null
          if (indexAttr !== null) {
            const index = Number.parseInt(indexAttr, 10)
            this.updateUIOnly(index)
          }
        }
      })
    }, options)

    // Re-observes all scroll targets whenever one is added or removed, so removed ones are not kept observed.
    effect(() => {
      this.observer?.disconnect()
      this.allPanels().forEach(ref => this.observer?.observe(ref.nativeElement))
    }, { injector: this.injector })
  }

  private updateUIOnly(index: number): void {
    if (this.navigationLockTimeout !== null) return
    if (this.currentIndex() === index) return
    this.currentIndex.set(index)
    this.sectionControl?.setValue(index.toString(), { emitEvent: false })
  }
}
